// apps/web/src/components/MainNav/MainNav.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useAuthUser } from '../../auth/useAuthUser';
import GemBalance from '../GemBalance/GemBalance';
import NavLevel from '../NavLevel/NavLevel';
import { useContinueLearning } from '../../shared/useContinueLearning';
import styles from './MainNav.module.scss';
import { useEscapeKey, useOutsideClick } from '../../hooks';

// Derive avatar initials / label from the Firebase user.
function avatarInitial(user: ReturnType<typeof useAuthUser>['user']): string {
	if (!user) return '';
	if (user.displayName?.trim()) {
		const parts = user.displayName.trim().split(/\s+/);
		const first = parts[0]?.[0] ?? '';
		const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
		return (first + last).toUpperCase();
	}
	if (user.email) return user.email[0].toUpperCase();
	return 'U';
}

// A menu row the current session can't reach — greyed, inert, honest about
// why (title). Mirrors the sidebar's navLocked treatment so "locked" reads
// the same everywhere.
const LockedMenuItem: React.FC<{ label: string }> = ({ label }) => (
	<span
		className={`${styles.menuItem} ${styles.menuItemLocked}`}
		role='menuitem'
		aria-disabled='true'
		title='Sign up to unlock'>
		<span>{label}</span>
		<span
			className={styles.menuLock}
			aria-hidden='true'>
			🔒
		</span>
	</span>
);

const guestIcon = (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.6'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'>
		<circle
			cx='10'
			cy='7'
			r='3.2'
		/>
		<path d='M4 17c.8-3 3.2-4.5 6-4.5s5.2 1.5 6 4.5' />
	</svg>
);

const MainNav: React.FC = () => {
	const { user, loading } = useAuthUser();
	const next = useContinueLearning();
	const navigate = useNavigate();

	const [menuOpen, setMenuOpen] = useState(false);
	// Google photoURLs (lh3.googleusercontent.com) can 404 / rate-limit.
	// If the <img> fails, fall back to initials instead of a broken glyph.
	const [imgFailed, setImgFailed] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	// Reset the image-failed flag whenever the user (and thus photoURL) changes,
	// so a new sign-in gets a fresh chance to load its photo.
	useEffect(() => {
		setImgFailed(false);
	}, [user?.photoURL]);

	// Close menu on outside click / Escape.
	useOutsideClick(menuRef, () => setMenuOpen(false), menuOpen);
	useEscapeKey(() => setMenuOpen(false), menuOpen);

	const handleSignOut = async () => {
		setMenuOpen(false);
		try {
			await signOut(auth);
			navigate('/login');
		} catch {
			/* noop — stay put if sign-out fails */
		}
	};

	const showPhoto = Boolean(user?.photoURL) && !imgFailed;

	return (
		<header
			className={styles.mainnav}
			role='banner'>
			{/* LEFT — brand / home */}
			<Link
				to='/'
				className={styles.brand}>
				<span className={styles.brandAccent}>Nisk</span>
			</Link>

			{/* CENTER — current progress + motivation. Authed only: these
			    are learn/economy widgets — dead doors for a visitor. */}
			{user !== null && (
				<div className={styles.center}>
				<Link
					to={next.href}
					className={styles.continue}>
					<span className={styles.continueLabel}>{next.label}</span>
					{next.context && (
						<span className={styles.continueContext}>
							{next.context}
						</span>
					)}
				</Link>
				{/* Streak lives inside the gem chip (see GemBalance) to keep the
				    bar uncluttered; the chip shows 🔥 N when a streak is active. */}
				<NavLevel />
				<GemBalance />
				</div>
			)}

			{/* RIGHT — account */}
			<div
				className={styles.user}
				ref={menuRef}>
				{loading ? (
					<div
						className={styles.avatarSkeleton}
						aria-hidden='true'
					/>
				) : user ? (
					<>
						<button
							type='button'
							className={styles.avatar}
							onClick={() => setMenuOpen((o) => !o)}
							aria-haspopup='menu'
							aria-expanded={menuOpen}
							aria-label='Account menu'>
							{showPhoto ? (
								<img
									src={user.photoURL as string}
									alt=''
									className={styles.avatarImg}
									referrerPolicy='no-referrer'
									onError={() => setImgFailed(true)}
								/>
							) : (
								// Fallback: initials on the dark circle. If you later want
								// a default raster/SVG image instead, render it here, e.g.:
								//   <img src={defaultAvatar} alt="" className={styles.avatarImg} />
								avatarInitial(user)
							)}
						</button>

						{menuOpen && (
							<div
								className={styles.menu}
								role='menu'>
								<div className={styles.menuHeader}>
									{user.displayName?.trim() && (
										<div className={styles.menuName}>
											{user.displayName}
										</div>
									)}
									<div className={styles.menuEmail}>
										{user.email ?? '—'}
									</div>
								</div>

								<div className={styles.menuDivider} />

								<Link
									to='/progress'
									className={styles.menuItem}
									role='menuitem'
									onClick={() => setMenuOpen(false)}>
									Badges
								</Link>
								<Link
									to='/settings'
									className={styles.menuItem}
									role='menuitem'
									onClick={() => setMenuOpen(false)}>
									Settings
								</Link>
								<Link
									to='/settings#export'
									className={styles.menuItem}
									role='menuitem'
									onClick={() => setMenuOpen(false)}>
									Export data
								</Link>
								<Link
									to='/legal/disclaimer'
									className={styles.menuItem}
									role='menuitem'
									onClick={() => setMenuOpen(false)}>
									Legal &amp; disclaimers
								</Link>

								<div className={styles.menuDivider} />

								<button
									type='button'
									className={`${styles.menuItem} ${styles.menuItemDanger}`}
									role='menuitem'
									onClick={handleSignOut}>
									Sign out
								</button>
							</div>
						)}
					</>
				) : (
					<div className={styles.authActions}>
						<Link
							to='/login'
							className={styles.authLogin}>
							Log in
						</Link>
						<Link
							to='/signup'
							className={styles.authSignup}>
							Sign up
						</Link>

						{/* Guest avatar: same menu surface as an account, with
						    account-only rows greyed + locked. The map shows what
						    signing up unlocks — honest, not hidden. */}
						<button
							type='button'
							className={`${styles.avatar} ${styles.avatarGuest}`}
							onClick={() => setMenuOpen((o) => !o)}
							aria-haspopup='menu'
							aria-expanded={menuOpen}
							aria-label='Guest menu'>
							{guestIcon}
						</button>

						{menuOpen && (
							<div
								className={styles.menu}
								role='menu'>
								<div className={styles.menuHeader}>
									<div className={styles.menuName}>Guest</div>
									<div className={styles.menuEmail}>
										Browsing without an account
									</div>
								</div>

								<div className={styles.menuDivider} />

								<LockedMenuItem label='Badges' />
								<LockedMenuItem label='Settings' />
								<LockedMenuItem label='Export data' />
								<Link
									to='/legal/disclaimer'
									className={styles.menuItem}
									role='menuitem'
									onClick={() => setMenuOpen(false)}>
									Legal &amp; disclaimers
								</Link>

								<div className={styles.menuDivider} />

								<Link
									to='/login'
									className={styles.menuItem}
									role='menuitem'
									onClick={() => setMenuOpen(false)}>
									Log in
								</Link>
								<Link
									to='/signup'
									className={`${styles.menuItem} ${styles.menuItemAccent}`}
									role='menuitem'
									onClick={() => setMenuOpen(false)}>
									Sign up — it&apos;s free
								</Link>
							</div>
						)}
					</div>
				)}
			</div>
		</header>
	);
};

export default MainNav;
